// src/pages/Security.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import useWebsiteStore from '../store/websiteStore';
import { websiteService } from '../api/services/websites';
import { useToastStore } from '../components/UI/Toast';
import { SecurityScoreCard, SecurityIssuesTable, SecurityRecommendations } from '../components/Security';

const Security = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [isScanning, setIsScanning] = useState(false);
    const { websites, fetchWebsites, loading: websitesLoading, error: websitesError } = useWebsiteStore();
    const addToast = useToastStore(state => state.addToast);

    // Načtení webů, pokud ještě nejsou načteny
    useEffect(() => {
        if (websites.length === 0 && !websitesLoading) {
            fetchWebsites();
        }
    }, [websites.length, websitesLoading, fetchWebsites]);

    // Načtení nejnovějších bezpečnostních dat pro všechny weby
    const { data: securityData, isLoading, error, refetch } = useQuery({
        queryKey: ['securityOverview'],
        queryFn: async () => {
            try {
                // Vytvoření pole slibů pro každý web
                const promises = websites.map(website =>
                    websiteService.getMonitoringResults(website.id, 'security', 1)
                        .then(response => ({
                            websiteId: website.id,
                            data: response.data && response.data.length > 0 ? response.data[0] : null
                        }))
                        .catch(err => ({
                            websiteId: website.id,
                            error: err,
                            data: null
                        }))
                );

                // Čekání na dokončení všech požadavků
                const results = await Promise.all(promises);

                // Zpracování výsledků
                return results.reduce((acc, item) => {
                    acc[item.websiteId] = item.data;
                    return acc;
                }, {});
            } catch (error) {
                console.error('Error fetching security data:', error);
                throw error;
            }
        },
        enabled: websites.length > 0, // Dotaz se spustí pouze pokud máme načtené weby
        staleTime: 5 * 60 * 1000, // Považuj data za aktuální po dobu 5 minut
    });

    // Funkce pro spuštění bezpečnostní kontroly pro konkrétní web
    const runSecurityCheck = async (websiteId) => {
        setIsScanning(true);
        try {
            await websiteService.runSecurityCheck(websiteId);
            addToast('Bezpečnostní kontrola byla úspěšně spuštěna', 'success');
            // Po krátké pauze obnovíme data
            setTimeout(() => {
                refetch();
                setIsScanning(false);
            }, 3000);
        } catch (error) {
            console.error('Chyba při spuštění bezpečnostní kontroly:', error);
            addToast('Nepodařilo se spustit bezpečnostní kontrolu', 'danger');
            setIsScanning(false);
        }
    };

    // Funkce pro spuštění bezpečnostní kontroly pro všechny weby
    const runAllSecurityChecks = async () => {
        setIsScanning(true);
        try {
            // Vytvoření pole slibů pro každý web
            const promises = websites.map(website =>
                websiteService.runSecurityCheck(website.id)
                    .catch(err => console.error(`Chyba při kontrole webu ${website.url}:`, err))
            );

            // Čekání na dokončení všech požadavků
            await Promise.all(promises);

            addToast('Bezpečnostní kontroly byly úspěšně spuštěny pro všechny weby', 'success');
            // Po krátké pauze obnovíme data
            setTimeout(() => {
                refetch();
                setIsScanning(false);
            }, 3000);
        } catch (error) {
            console.error('Chyba při spuštění bezpečnostních kontrol:', error);
            addToast('Některé bezpečnostní kontroly se nepodařilo spustit', 'warning');
            setIsScanning(false);
        }
    };

    // Pomocná funkce pro získání variant badgí podle závažnosti
    const getSeverityVariant = (severity) => {
        switch (severity) {
            case 'critical':
            case 'high':
                return 'danger';
            case 'medium':
                return 'warning';
            case 'low':
                return 'info';
            default:
                return 'secondary';
        }
    };

    // Pomocná funkce pro překlad závažnosti
    const getSeverityText = (severity) => {
        switch (severity) {
            case 'critical':
                return 'Kritická';
            case 'high':
                return 'Vysoká';
            case 'medium':
                return 'Střední';
            case 'low':
                return 'Nízká';
            default:
                return 'Neznámá';
        }
    };

    // Funkce pro výpočet celkového bezpečnostního skóre
    const calculateOverallSecurityScore = () => {
        if (!securityData || Object.keys(securityData).length === 0) {
            return 'N/A';
        }

        let totalScore = 0;
        let websitesWithScore = 0;

        Object.values(securityData).forEach(websiteData => {
            if (websiteData && websiteData.details) {
                // Počítáme skóre na základě počtu a závažnosti problémů
                let score = 100;
                const issues = websiteData.details.issues || [];

                issues.forEach(issue => {
                    if (issue.severity === 'critical') score -= 20;
                    else if (issue.severity === 'high') score -= 15;
                    else if (issue.severity === 'medium') score -= 10;
                    else if (issue.severity === 'low') score -= 5;
                });

                totalScore += Math.max(0, score);
                websitesWithScore++;
            }
        });

        return websitesWithScore > 0
            ? Math.round(totalScore / websitesWithScore)
            : 'N/A';
    };

    // Funkce pro získání celkového počtu problémů podle závažnosti
    const getIssueCountsBySeverity = () => {
        const counts = {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            total: 0
        };

        if (!securityData) return counts;

        Object.values(securityData).forEach(websiteData => {
            if (websiteData && websiteData.details && websiteData.details.issues) {
                websiteData.details.issues.forEach(issue => {
                    if (issue.severity === 'critical') counts.critical++;
                    else if (issue.severity === 'high') counts.high++;
                    else if (issue.severity === 'medium') counts.medium++;
                    else if (issue.severity === 'low') counts.low++;

                    counts.total++;
                });
            }
        });

        return counts;
    };

    // Funkce pro získání webů s problémy podle závažnosti
    const getWebsitesWithIssues = (minSeverity) => {
        if (!securityData || !websites) return [];

        // Mapování závažností na číselné hodnoty pro porovnání
        const severityValues = {
            'critical': 4,
            'high': 3,
            'medium': 2,
            'low': 1
        };

        const minSeverityValue = severityValues[minSeverity] || 0;

        return websites.filter(website => {
            const websiteSecurityData = securityData[website.id];
            if (!websiteSecurityData || !websiteSecurityData.details || !websiteSecurityData.details.issues) {
                return false;
            }

            return websiteSecurityData.details.issues.some(issue =>
                (severityValues[issue.severity] || 0) >= minSeverityValue
            );
        });
    };

    // Data pro zobrazení
    const overallScore = calculateOverallSecurityScore();
    const issueCounts = getIssueCountsBySeverity();
    const websitesWithCriticalIssues = getWebsitesWithIssues('critical');
    const websitesWithHighIssues = getWebsitesWithIssues('high');

    // Nejčastější bezpečnostní problémy
    const getCommonIssues = () => {
        if (!securityData) return [];

        const issueTypes = {};

        Object.values(securityData).forEach(websiteData => {
            if (websiteData && websiteData.details && websiteData.details.issues) {
                websiteData.details.issues.forEach(issue => {
                    const type = issue.type;
                    if (!issueTypes[type]) {
                        issueTypes[type] = {
                            type,
                            count: 0,
                            severity: issue.severity,
                            example: issue.message
                        };
                    }
                    issueTypes[type].count++;
                    // Aktualizujeme nejvyšší závažnost pro tento typ problému
                    const severityValues = { critical: 4, high: 3, medium: 2, low: 1 };
                    if (severityValues[issue.severity] > severityValues[issueTypes[type].severity]) {
                        issueTypes[type].severity = issue.severity;
                    }
                });
            }
        });

        return Object.values(issueTypes)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    };

    const commonIssues = getCommonIssues();

    if (websitesLoading || isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání bezpečnostních dat...</span>
                </Spinner>
            </div>
        );
    }

    if (websitesError || error) {
        return (
            <Alert variant="danger" className="m-4">
                <Alert.Heading>Došlo k chybě</Alert.Heading>
                <p>{websitesError || error}</p>
            </Alert>
        );
    }

    if (websites.length === 0) {
        return (
            <Container fluid>
                <h1 className="mb-4">Bezpečnost</h1>
                <Alert variant="info">
                    <Alert.Heading>Žádné weby</Alert.Heading>
                    <p>Nejsou k dispozici žádné weby pro kontrolu bezpečnosti. Přidejte nejprve nějaký web v sekci Weby.</p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Bezpečnost</h1>
                <Button
                    variant="primary"
                    onClick={runAllSecurityChecks}
                    disabled={isScanning}
                >
                    {isScanning ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Probíhá skenování...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-shield-alt me-2"></i> Spustit kontrolu všech webů
                        </>
                    )}
                </Button>
            </div>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
            >
                <Tab eventKey="overview" title="Přehled">
                    <Row className="mb-4">
                        <Col lg={3} md={6} className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <Card.Body className="p-4 text-center">
                                    <h5 className="text-muted mb-3">Celkové skóre bezpečnosti</h5>
                                    <div className={`score-circle mx-auto border-${
                                        overallScore === 'N/A' ? 'secondary' :
                                            overallScore >= 90 ? 'success' :
                                                overallScore >= 70 ? 'warning' : 'danger'
                                    }`}>
                                        <div className="score-value">{overallScore}</div>
                                        {overallScore !== 'N/A' && <div className="score-max">/100</div>}
                                    </div>
                                    {overallScore !== 'N/A' && (
                                        <Badge
                                            bg={
                                                overallScore >= 90 ? 'success' :
                                                    overallScore >= 70 ? 'warning' : 'danger'
                                            }
                                            className="mt-3 px-3 py-2"
                                        >
                                            {overallScore >= 90 ? 'Výborné' :
                                                overallScore >= 70 ? 'Dobré' : 'Rizikové'}
                                        </Badge>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={9} md={6} className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <Card.Body className="p-4">
                                    <h5 className="mb-3">Bezpečnostní problémy</h5>
                                    <Row className="text-center">
                                        <Col xs={3}>
                                            <div className="p-3 bg-danger bg-opacity-10 rounded mb-2">
                                                <h2 className="text-danger mb-0">{issueCounts.critical}</h2>
                                            </div>
                                            <div className="text-muted">Kritické</div>
                                        </Col>
                                        <Col xs={3}>
                                            <div className="p-3 bg-danger bg-opacity-10 rounded mb-2">
                                                <h2 className="text-danger mb-0">{issueCounts.high}</h2>
                                            </div>
                                            <div className="text-muted">Vysoké</div>
                                        </Col>
                                        <Col xs={3}>
                                            <div className="p-3 bg-warning bg-opacity-10 rounded mb-2">
                                                <h2 className="text-warning mb-0">{issueCounts.medium}</h2>
                                            </div>
                                            <div className="text-muted">Střední</div>
                                        </Col>
                                        <Col xs={3}>
                                            <div className="p-3 bg-info bg-opacity-10 rounded mb-2">
                                                <h2 className="text-info mb-0">{issueCounts.low}</h2>
                                            </div>
                                            <div className="text-muted">Nízké</div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {(websitesWithCriticalIssues.length > 0 || websitesWithHighIssues.length > 0) && (
                        <Row className="mb-4">
                            <Col>
                                <Card className="shadow-sm">
                                    <Card.Header className="bg-white">
                                        <h5 className="mb-0">Weby s kritickými a vysokými bezpečnostními riziky</h5>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Table responsive hover className="mb-0">
                                            <thead>
                                            <tr>
                                                <th>Web</th>
                                                <th>Kritické</th>
                                                <th>Vysoké</th>
                                                <th>Poslední kontrola</th>
                                                <th>Akce</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {[...websitesWithCriticalIssues, ...websitesWithHighIssues.filter(w =>
                                                !websitesWithCriticalIssues.some(cw => cw.id === w.id)
                                            )].map(website => {
                                                const websiteSecurityData = securityData[website.id];
                                                if (!websiteSecurityData) return null;

                                                const issues = websiteSecurityData.details?.issues || [];
                                                const criticalCount = issues.filter(i => i.severity === 'critical').length;
                                                const highCount = issues.filter(i => i.severity === 'high').length;

                                                return (
                                                    <tr key={website.id}>
                                                        <td>
                                                            <a href={`/websites/${website.id}`} className="text-decoration-none">
                                                                {website.name || website.url}
                                                            </a>
                                                        </td>
                                                        <td>
                                                            {criticalCount > 0 && (
                                                                <Badge bg="danger" pill>{criticalCount}</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {highCount > 0 && (
                                                                <Badge bg="danger" pill>{highCount}</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {websiteSecurityData.createdAt ?
                                                                new Date(websiteSecurityData.createdAt).toLocaleString() :
                                                                'Nikdy'}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => runSecurityCheck(website.id)}
                                                                disabled={isScanning}
                                                            >
                                                                <i className="fas fa-shield-alt me-1"></i> Kontrola
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow-sm">
                                <Card.Header className="bg-white">
                                    <h5 className="mb-0">Nejčastější bezpečnostní problémy</h5>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {commonIssues.length > 0 ? (
                                        <Table responsive hover className="mb-0">
                                            <thead>
                                            <tr>
                                                <th>Problém</th>
                                                <th>Závažnost</th>
                                                <th>Počet výskytů</th>
                                                <th>Příklad</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {commonIssues.map((issue, index) => (
                                                <tr key={index}>
                                                    <td>{issue.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                                                    <td>
                                                        <Badge bg={getSeverityVariant(issue.severity)}>
                                                            {getSeverityText(issue.severity)}
                                                        </Badge>
                                                    </td>
                                                    <td>{issue.count}</td>
                                                    <td>{issue.example}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="fas fa-shield-alt text-success fa-3x mb-3"></i>
                                            <h5>Žádné častější bezpečnostní problémy</h5>
                                            <p className="text-muted">Vaše weby jsou buď bezpečné, nebo nebyly ještě zkontrolovány.</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                <Tab eventKey="all-websites" title="Všechny weby">
                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            <Table responsive hover className="mb-0">
                                <thead>
                                <tr>
                                    <th>Web</th>
                                    <th>Skóre</th>
                                    <th>Problémů</th>
                                    <th>Kritické</th>
                                    <th>Vysoké</th>
                                    <th>Střední</th>
                                    <th>Nízké</th>
                                    <th>Poslední kontrola</th>
                                    <th>Akce</th>
                                </tr>
                                </thead>
                                <tbody>
                                {websites.map(website => {
                                    const websiteSecurityData = securityData?.[website.id];
                                    const hasData = websiteSecurityData && websiteSecurityData.details;
                                    let securityScore = 'N/A';
                                    let issues = [];

                                    if (hasData) {
                                        issues = websiteSecurityData.details.issues || [];
                                        // Výpočet skóre na základě závažnosti problémů
                                        let score = 100;
                                        issues.forEach(issue => {
                                            if (issue.severity === 'critical') score -= 20;
                                            else if (issue.severity === 'high') score -= 15;
                                            else if (issue.severity === 'medium') score -= 10;
                                            else if (issue.severity === 'low') score -= 5;
                                        });
                                        securityScore = Math.max(0, score);
                                    }

                                    const criticalCount = hasData ? issues.filter(i => i.severity === 'critical').length : 0;
                                    const highCount = hasData ? issues.filter(i => i.severity === 'high').length : 0;
                                    const mediumCount = hasData ? issues.filter(i => i.severity === 'medium').length : 0;
                                    const lowCount = hasData ? issues.filter(i => i.severity === 'low').length : 0;

                                    return (
                                        <tr key={website.id}>
                                            <td>
                                                <a href={`/websites/${website.id}`} className="text-decoration-none">
                                                    {website.name || website.url}
                                                </a>
                                            </td>
                                            <td>
                                                {securityScore !== 'N/A' ? (
                                                    <Badge
                                                        bg={
                                                            securityScore >= 90 ? 'success' :
                                                                securityScore >= 70 ? 'warning' : 'danger'
                                                        }
                                                    >
                                                        {securityScore}
                                                    </Badge>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td>{hasData ? issues.length : 'N/A'}</td>
                                            <td>
                                                {criticalCount > 0 && (
                                                    <Badge bg="danger" pill>{criticalCount}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {highCount > 0 && (
                                                    <Badge bg="danger" pill>{highCount}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {mediumCount > 0 && (
                                                    <Badge bg="warning" pill>{mediumCount}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {lowCount > 0 && (
                                                    <Badge bg="info" pill>{lowCount}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {hasData ?
                                                    new Date(websiteSecurityData.createdAt).toLocaleString() :
                                                    'Nikdy'}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => runSecurityCheck(website.id)}
                                                    disabled={isScanning}
                                                >
                                                    <i className="fas fa-shield-alt me-1"></i> Kontrola
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </Container>
    );
};

export default Security;